import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { NETWORK_ERROR_MESSAGE, documentReadFailureMessage } from '../api/messages';
import {
  decodeTextBytes,
  documentPreviewPath,
  fetchDocumentPreview,
  isServerRenderedImage,
  usesDocumentPreview,
  type DocumentPreview,
} from './document-preview';
import { renditionPath } from './use-buyer-copy';

/** How the viewer should render a document. */
export type PreviewKind = 'pdf' | 'image' | 'text' | 'office' | 'unsupported';

/**
 * Decide the render mode from the response Content-Type. The header may carry
 * parameters (e.g. `text/plain; charset=utf-8`) or arrive empty, so we match on
 * the leading type/subtype only, lower-cased.
 *   - `application/pdf`                  → the in-app pdf.js canvas viewer (ADR-0027)
 *   - `image/tiff|heic|heif`             → `office`: rendered as pages by the preview endpoint
 *   - `image/*`                          → inline <img>
 *   - `text/plain | csv | markdown`      → scrollable <pre>
 *   - anything else                      → "preview not available" + Download
 * Office files (xlsx, docx, csv, …) never reach this: the content endpoint
 * serves them as an octet-stream attachment, so they are shown through the
 * preview endpoint instead (`usesDocumentPreview`, ADR-0033).
 */
export function previewKindFor(contentType: string | null | undefined): PreviewKind {
  const ct = (contentType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
  if (ct === 'application/pdf') return 'pdf';
  if (isServerRenderedImage(ct)) return 'office';
  if (ct.startsWith('image/')) return 'image';
  if (
    ct === 'text/plain' ||
    ct === 'text/csv' ||
    ct === 'text/markdown' ||
    ct === 'text/x-markdown'
  ) {
    return 'text';
  }
  return 'unsupported';
}

export interface LoadedContent {
  /**
   * Where the Download button points: an object URL for the fetched blob, or —
   * for an office preview, whose bytes were never fetched — the content
   * endpoint's `?download=1` (the server makes and audits that decision; the
   * room admin may always download). In buyer-copy mode it is the redacted
   * copy's rendition endpoint — never the original.
   */
  url: string;
  kind: PreviewKind;
  /** Decoded text, only for `kind === 'text'`. */
  text?: string;
  /** The raw bytes, only for `kind === 'pdf'` (handed to pdf.js). */
  data?: ArrayBuffer;
  /** The server-built preview, only for `kind === 'office'`. */
  preview?: DocumentPreview;
}

export interface DocumentPreviewState {
  content: LoadedContent | null;
  error: string | null;
  loading: boolean;
}

/**
 * Fetch one room document for the admin's in-app viewer and classify it for
 * rendering. Used by the preview modal and the full-page review queue.
 *
 *   - Office files (by name: xlsx, xls, csv, docx, pptx, …) → the read-only
 *     JSON preview, GET /api/v1/rooms/:roomId/documents/:documentId/preview
 *     (ADR-0033) — a sheet grid, Word HTML or text.
 *   - Everything else → the bytes from the same-origin, cookie-authenticated
 *     content endpoint GET …/content, read as a Blob → object URL (BINARY, so
 *     the raw credentialed `fetch`, not the JSON api client).
 *
 * The object URL is revoked on unmount / when the document changes so no blob
 * leaks, and a switched document never shows the previous one's content.
 */
export function useDocumentPreview(
  roomId: string,
  documentId: string,
  filename?: string,
  /** False: load nothing (the data-room viewer is showing another copy, or a
   * "Being prepared" notice). */
  enabled = true,
  /** Preview the BUYER COPY (the redacted Word / Excel copy) instead of the
   * stored file — always through the preview endpoint, and never a fallback to
   * the original's bytes. */
  buyerCopy = false,
): DocumentPreviewState {
  const [content, setContent] = useState<LoadedContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(enabled);
  const office = buyerCopy || usesDocumentPreview(filename);

  useEffect(() => {
    if (!enabled) {
      setContent(null);
      setError(null);
      setLoading(false);
      return;
    }
    let objectUrl: string | null = null;
    const controller = new AbortController();
    // The inline viewer asks for a scanned PDF's searchable variant (ADR-0057;
    // the server serves the original when there is none, or to anyone else).
    const contentUrl = `/api/v1/rooms/${roomId}/documents/${documentId}/content`;
    const notFound = 'This document is not available to view.';
    setLoading(true);
    setError(null);
    setContent(null);

    const loadPreview = (fallBackToBytes: boolean): Promise<void> =>
      fetchDocumentPreview(
        documentPreviewPath(roomId, documentId, { buyerCopy }),
        controller.signal,
        notFound,
      ).then((preview) => {
        if (controller.signal.aborted) return;
        if (
          preview.kind === 'unsupported' &&
          preview.reason === 'type' &&
          fallBackToBytes &&
          !buyerCopy
        ) {
          return loadBytes();
        }
        // Buyer-copy mode never points anything at the original: its Download is
        // the redacted copy's own file (the rendition endpoint), never …/content.
        const url = buyerCopy ? renditionPath(roomId, documentId) : `${contentUrl}?download=1`;
        setContent({ url, kind: 'office', preview });
        setLoading(false);
      });

    const loadBytes = (): Promise<void> =>
      fetch(`${contentUrl}?searchable=1`, {
        credentials: 'include',
        signal: controller.signal,
      }).then(async (res) => {
        if (!res.ok) {
          // 503 = a storage read failure (temporary), not a missing document.
          throw new ApiError(res.status, documentReadFailureMessage(res.status, notFound));
        }
        const blob = await res.blob();
        const contentType = res.headers.get('Content-Type') ?? blob.type;
        const kind = previewKindFor(contentType);
        // A TIFF / HEIC whose name did not say so: the server renders its pages.
        if (kind === 'office') return loadPreview(false);
        const text = kind === 'text' ? decodeTextBytes(await blob.arrayBuffer()) : undefined;
        const data = kind === 'pdf' ? await blob.arrayBuffer() : undefined;
        // The viewer may have switched documents (or closed) while the bytes /
        // text were read: aborting does not stop an already-resolved blob, so
        // without this check the OLD document's text would land on the new one
        // (and its object URL would never be revoked).
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setContent({ url: objectUrl, kind, text, data });
        setLoading(false);
      });

    // The extension picked the preview, but the server decides the format from
    // the stored type: a PDF saved as "….docx" answers `unsupported/type` there,
    // and is then shown from its bytes like any other PDF.
    const load = office ? loadPreview(true) : loadBytes();

    load.catch((err: unknown) => {
      // A fetch aborted by the cleanup below is expected (close/unmount, or
      // StrictMode's double-invoke) — not an error to surface.
      if (controller.signal.aborted) return;
      // Our own status messages are ApiErrors; anything else is the fetch or
      // body read failing (a network drop) — never show its raw text.
      setError(err instanceof ApiError ? err.message : NETWORK_ERROR_MESSAGE);
      setLoading(false);
    });

    // Revoke the object URL on close/unmount (or document change) so the blob is
    // released and the URL cannot be reused.
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [roomId, documentId, office, enabled, buyerCopy]);

  return { content, error, loading };
}
